import type {
  BuyerVisibility,
  InspectionIssue,
  InspectionReportPayload,
  NormalizedSeverity,
  OverviewCostTotals,
  OverviewPriorityIssue,
  SelectedSystemNegotiationTotals,
  SystemCostTotals,
  SystemSummary,
  SystemTypeRollup,
  TimeframeBucket,
  TimeframeCostTotals,
  WorkspaceState,
} from '../types/report';
import { formatSystemLabel, timeframeRank } from './format';

export const severityRank: Record<NormalizedSeverity, number> = {
  critical: 0,
  moderate: 1,
  minor: 2,
};

export const timeframeBucketRank: Record<TimeframeBucket, number> = {
  beforeClose: 0,
  nextMonth: 1,
  afterMoveIn: 2,
  later: 3,
};

export const timeframeBucketLabels: Record<TimeframeBucket, string> = {
  beforeClose: 'Before close',
  nextMonth: 'Next 30 days',
  afterMoveIn: 'After move-in',
  later: 'Later / monitor',
};

const emptyCostTotals = (): OverviewCostTotals => ({
  issueNumbers: [],
  issueCount: 0,
  minTotal: 0,
  maxTotal: 0,
  avgTotal: 0,
});

const normalizeSeverityValue = (value?: string | null): NormalizedSeverity | null => {
  const normalized = (value || '').toLowerCase().replace(/[_-]/g, ' ');
  if (normalized === 'critical' || normalized.includes('severe') || normalized.includes('life')) return 'critical';
  if (normalized.includes('moderate')) return 'moderate';
  if (normalized === 'minor' || normalized.includes('low')) return 'minor';
  return null;
};

export const normalizeSeverity = (issue: InspectionIssue): NormalizedSeverity => {
  return normalizeSeverityValue(issue.derived?.normalizedSeverity) || normalizeSeverityValue(issue.severity?.score) || 'minor';
};

export const issueCostMin = (issue: InspectionIssue): number =>
  issue.derived?.estimatedCostMin ?? issue.estimatedCost?.min ?? 0;

export const issueCostMax = (issue: InspectionIssue): number =>
  issue.derived?.estimatedCostMax ?? issue.estimatedCost?.max ?? issueCostMin(issue);

export const issueCostAvg = (issue: InspectionIssue): number =>
  issue.derived?.estimatedCostAvg ?? Math.round((issueCostMin(issue) + issueCostMax(issue)) / 2);

export const issueIsSafety = (issue: InspectionIssue): boolean => Boolean(issue.derived?.safety ?? issue.safety?.concern);

export const byIssueNumber = (payload: InspectionReportPayload): Map<number, InspectionIssue> =>
  new Map(payload.issueList.map((issue) => [issue.issueNumber, issue]));

const addIssueCostToTotals = (totals: OverviewCostTotals, issue: InspectionIssue): void => {
  totals.issueNumbers.push(issue.issueNumber);
  totals.issueCount += 1;
  totals.minTotal += issueCostMin(issue);
  totals.maxTotal += issueCostMax(issue);
  totals.avgTotal += issueCostAvg(issue);
};

const getIssueTimeframeText = (issue: InspectionIssue): string | undefined =>
  issue.derived?.normalizedTimeframe || issue.recommendedTimeframe;

export const normalizeTimeframeBucket = (timeframe?: string | null): TimeframeBucket => {
  const normalized = (timeframe || '').toLowerCase().replace(/[_-]/g, ' ');
  if (
    normalized.includes('before close') ||
    normalized.includes('closing') ||
    normalized.includes('immediate') ||
    normalized.includes('urgent')
  ) {
    return 'beforeClose';
  }
  if (normalized.includes('next month') || normalized.includes('30')) return 'nextMonth';
  if (normalized.includes('after move') || normalized.includes('60') || normalized.includes('90')) return 'afterMoveIn';
  return 'later';
};

export const getIssueTimeframeBucket = (issue: InspectionIssue): TimeframeBucket =>
  normalizeTimeframeBucket(getIssueTimeframeText(issue));

export const getSeverityCounts = (payload: InspectionReportPayload): Record<NormalizedSeverity, number> => {
  const counts: Record<NormalizedSeverity, number> = { critical: 0, moderate: 0, minor: 0 };
  const rollupCounts = payload.rollups?.severityCounts;
  if (rollupCounts) {
    Object.entries(rollupCounts).forEach(([key, count]) => {
      if (key.toLowerCase() === 'total') return;
      const severity = normalizeSeverityValue(key);
      if (severity) counts[severity] += count;
    });
    return counts;
  }

  payload.issueList.forEach((issue) => {
    counts[normalizeSeverity(issue)] += 1;
  });
  return counts;
};

export const getIssuesSortedByPriority = (issues: InspectionIssue[]): InspectionIssue[] =>
  [...issues].sort((a, b) => {
    const severityDelta = severityRank[normalizeSeverity(a)] - severityRank[normalizeSeverity(b)];
    if (severityDelta !== 0) return severityDelta;
    const timeframeDelta =
      timeframeBucketRank[getIssueTimeframeBucket(a)] - timeframeBucketRank[getIssueTimeframeBucket(b)] ||
      timeframeRank(getIssueTimeframeText(a)) - timeframeRank(getIssueTimeframeText(b));
    if (timeframeDelta !== 0) return timeframeDelta;
    return issueCostAvg(b) - issueCostAvg(a);
  });

export const getRecommendedNegotiationIssueNumbers = (payload: InspectionReportPayload): number[] => {
  const issueGroups = payload.rollups?.issueGroups || {};
  const seeded = new Set<number>([
    ...(issueGroups.criticalIssues || []),
    ...(issueGroups.urgentIssues || []),
    ...(issueGroups.highStakesIssues || []),
  ]);

  payload.issueList.forEach((issue) => {
    const categories = issue.derived?.categories || [];
    const tags = (issue.derived?.tags || []).map((tag) => tag.name);
    if (
      issueIsSafety(issue) ||
      issue.specialist_evaluation_needed ||
      issueCostAvg(issue) >= 800 ||
      categories.some((category) => ['critical_safety', 'active_damage', 'major_cost'].includes(category)) ||
      tags.some((tag) => ['urgent_repair', 'specialist', 'high_cost'].includes(tag))
    ) {
      seeded.add(issue.issueNumber);
    }
  });

  return getIssuesSortedByPriority(payload.issueList)
    .filter((issue) => seeded.has(issue.issueNumber))
    .map((issue) => issue.issueNumber);
};

const getSystemRollupIssueNumbers = (rollup?: SystemTypeRollup): number[] => {
  if (!rollup) return [];
  if (Array.isArray(rollup)) return rollup;
  return rollup.issueNumbers || rollup.issues || [];
};

const getSystemRollupIssueCount = (rollup: SystemTypeRollup | undefined, issueNumbers: number[]): number => {
  if (!rollup || Array.isArray(rollup)) return issueNumbers.length;
  return rollup.issueCount ?? rollup.count ?? issueNumbers.length;
};

const getSystemRollupPassCount = (rollup?: SystemTypeRollup): number | undefined => {
  if (!rollup || Array.isArray(rollup)) return undefined;
  return rollup.passCount ?? rollup.passedCount ?? rollup.noIssueCount ?? rollup.okCount;
};

const getSystemRollupSeverityCounts = (rollup?: SystemTypeRollup): Record<NormalizedSeverity, number> | null => {
  if (!rollup || Array.isArray(rollup) || !rollup.severityCounts) return null;
  const counts: Record<NormalizedSeverity, number> = { critical: 0, moderate: 0, minor: 0 };
  Object.entries(rollup.severityCounts).forEach(([key, count]) => {
    const severity = normalizeSeverityValue(key);
    if (severity) counts[severity] += count;
  });
  return counts;
};

const getWorstSeverityFromCounts = (counts: Record<NormalizedSeverity, number>): NormalizedSeverity => {
  if (counts.critical > 0) return 'critical';
  if (counts.moderate > 0) return 'moderate';
  return 'minor';
};

export const getSystemSummaries = (payload: InspectionReportPayload): SystemSummary[] => {
  const groups = payload.rollups?.systemGroups || {};
  const issuesBySystemType = payload.rollups?.issuesBySystemType || {};
  const issueMap = byIssueNumber(payload);
  const systemIds = new Set<string>([
    ...Object.keys(payload.system_condition_summary || {}),
    ...Object.keys(groups),
    ...Object.keys(issuesBySystemType),
    ...payload.issueList.map((issue) => issue.systemCategory || 'miscellaneousSystems'),
  ]);

  return [...systemIds].map((id) => {
    const fromRollup = groups[id] || [];
    const systemTypeRollup = issuesBySystemType[id];
    const fromSystemTypeRollup = getSystemRollupIssueNumbers(systemTypeRollup);
    const fromIssues = payload.issueList
      .filter((issue) => (issue.systemCategory || 'miscellaneousSystems') === id)
      .map((issue) => issue.issueNumber);
    const issueNumbers = [...new Set([...fromRollup, ...fromSystemTypeRollup, ...fromIssues])];
    const issues = issueNumbers.map((issueNumber) => issueMap.get(issueNumber)).filter(Boolean) as InspectionIssue[];
    const severityCounts = getSystemRollupSeverityCounts(systemTypeRollup) || issues.reduce<Record<NormalizedSeverity, number>>(
      (counts, issue) => {
        counts[normalizeSeverity(issue)] += 1;
        return counts;
      },
      { critical: 0, moderate: 0, minor: 0 },
    );
    const worstSeverity = getWorstSeverityFromCounts(severityCounts);
    const explicitIssueCount = getSystemRollupIssueCount(systemTypeRollup, issueNumbers);
    const issueCount = Math.max(issueNumbers.length, explicitIssueCount);
    const passCount = getSystemRollupPassCount(systemTypeRollup);
    const costMin = issues.reduce((sum, issue) => sum + issueCostMin(issue), 0);
    const costMax = issues.reduce((sum, issue) => sum + issueCostMax(issue), 0);
    const costAvg = issues.reduce((sum, issue) => sum + issueCostAvg(issue), 0);

    return {
      id,
      label: formatSystemLabel(id),
      issueNumbers,
      issueCount,
      worstSeverity,
      worstSeverityCount: severityCounts[worstSeverity],
      urgentCount: issues.filter((issue) => timeframeBucketRank[getIssueTimeframeBucket(issue)] <= timeframeBucketRank.nextMonth).length,
      costMin,
      costMax,
      costAvg,
      costExposure: costMax,
      passCount,
      totalCount: !systemTypeRollup || Array.isArray(systemTypeRollup) ? undefined : systemTypeRollup.totalCount,
      status: !systemTypeRollup || Array.isArray(systemTypeRollup) ? undefined : systemTypeRollup.status,
      summary: payload.system_condition_summary?.[id] || (issueCount ? 'Issues noted during inspection.' : 'No reported issues.'),
    };
  }).sort((a, b) => {
    const severityDelta = severityRank[a.worstSeverity] - severityRank[b.worstSeverity];
    if (severityDelta !== 0) return severityDelta;
    const worstSeverityCountDelta = b.worstSeverityCount - a.worstSeverityCount;
    if (worstSeverityCountDelta !== 0) return worstSeverityCountDelta;
    const urgentDelta = b.urgentCount - a.urgentCount;
    if (urgentDelta !== 0) return urgentDelta;
    const exposureDelta = b.costExposure - a.costExposure;
    if (exposureDelta !== 0) return exposureDelta;
    const issueCountDelta = b.issueCount - a.issueCount;
    if (issueCountDelta !== 0) return issueCountDelta;
    return a.label.localeCompare(b.label);
  });
};

export const getHomeAge = (payload: InspectionReportPayload, currentYear = new Date().getFullYear()): number | null => {
  const yearBuilt = Number.parseInt(payload.summary.yearBuilt || '', 10);
  if (!Number.isFinite(yearBuilt) || yearBuilt <= 0 || yearBuilt > currentYear) return null;
  return currentYear - yearBuilt;
};

export const getCostTotalsByTimeframe = (payload: InspectionReportPayload): Record<TimeframeBucket, TimeframeCostTotals> => {
  const totals: Record<TimeframeBucket, TimeframeCostTotals> = {
    beforeClose: { ...emptyCostTotals(), bucket: 'beforeClose', label: timeframeBucketLabels.beforeClose },
    nextMonth: { ...emptyCostTotals(), bucket: 'nextMonth', label: timeframeBucketLabels.nextMonth },
    afterMoveIn: { ...emptyCostTotals(), bucket: 'afterMoveIn', label: timeframeBucketLabels.afterMoveIn },
    later: { ...emptyCostTotals(), bucket: 'later', label: timeframeBucketLabels.later },
  };

  payload.issueList.forEach((issue) => {
    addIssueCostToTotals(totals[getIssueTimeframeBucket(issue)], issue);
  });

  return totals;
};

export const getCostTotalsBySystem = (payload: InspectionReportPayload): SystemCostTotals[] => {
  const issueMap = byIssueNumber(payload);
  return getSystemSummaries(payload).map((system) => {
    const totals = emptyCostTotals();
    system.issueNumbers.forEach((issueNumber) => {
      const issue = issueMap.get(issueNumber);
      if (issue) addIssueCostToTotals(totals, issue);
    });

    return {
      ...totals,
      systemId: system.id,
      label: system.label,
      issueCount: system.issueCount,
    };
  });
};

export const getSelectedNegotiationTotalsBySystem = (
  payload: InspectionReportPayload,
  workspace: WorkspaceState,
): SelectedSystemNegotiationTotals[] => {
  const issueMap = byIssueNumber(payload);
  const bySystem = new Map<string, SelectedSystemNegotiationTotals>();

  workspace.negotiation_builder.selectedIssueNumbers.forEach((issueNumber) => {
    const issue = issueMap.get(issueNumber);
    if (!issue) return;
    const systemId = issue.systemCategory || 'miscellaneousSystems';
    const existing = bySystem.get(systemId) || {
      ...emptyCostTotals(),
      systemId,
      label: formatSystemLabel(systemId),
      targetTotal: 0,
    };

    addIssueCostToTotals(existing, issue);
    existing.targetTotal += workspace.negotiation_builder.costOverrides[String(issue.issueNumber)] ?? issueCostAvg(issue);
    bySystem.set(systemId, existing);
  });

  return [...bySystem.values()].sort((a, b) => {
    const targetDelta = b.targetTotal - a.targetTotal;
    if (targetDelta !== 0) return targetDelta;
    const issueCountDelta = b.issueCount - a.issueCount;
    if (issueCountDelta !== 0) return issueCountDelta;
    return a.label.localeCompare(b.label);
  });
};

export const getTopPriorityIssues = (payload: InspectionReportPayload, limit = 5): OverviewPriorityIssue[] =>
  getIssuesSortedByPriority(payload.issueList)
    .slice(0, limit)
    .map((issue) => {
      const systemId = issue.systemCategory || 'miscellaneousSystems';
      const timeframeBucket = getIssueTimeframeBucket(issue);
      return {
        issue,
        issueNumber: issue.issueNumber,
        title: issue.title,
        systemId,
        systemLabel: formatSystemLabel(systemId),
        severity: normalizeSeverity(issue),
        timeframeBucket,
        timeframeLabel: timeframeBucketLabels[timeframeBucket],
        costMin: issueCostMin(issue),
        costMax: issueCostMax(issue),
        costAvg: issueCostAvg(issue),
        urgent: timeframeBucketRank[timeframeBucket] <= timeframeBucketRank.nextMonth,
      };
    });

export const getNegotiationTotals = (payload: InspectionReportPayload, workspace: WorkspaceState) => {
  const issueMap = byIssueNumber(payload);
  const selected = workspace.negotiation_builder.selectedIssueNumbers
    .map((issueNumber) => issueMap.get(issueNumber))
    .filter(Boolean) as InspectionIssue[];

  const target = selected.reduce((sum, issue) => {
    const override = workspace.negotiation_builder.costOverrides[String(issue.issueNumber)];
    return sum + (override ?? issueCostAvg(issue));
  }, 0);

  return {
    selected,
    openingAsk: Math.round(target * 1.15),
    targetAsk: target,
    walkAwayMinimum: Math.round(target * 0.7),
  };
};

export const getLeverageScore = (payload: InspectionReportPayload, workspace: WorkspaceState): number => {
  const { selected, targetAsk } = getNegotiationTotals(payload, workspace);
  if (!selected.length) return 1;
  const safetyCount = selected.filter(issueIsSafety).length;
  const specialistCount = selected.filter((issue) => issue.specialist_evaluation_needed || issue.derived?.specialistNeeded).length;
  const urgentCount = selected.filter((issue) => timeframeBucketRank[getIssueTimeframeBucket(issue)] <= timeframeBucketRank.nextMonth).length;
  const score = 1 + safetyCount + specialistCount + urgentCount + Math.floor(targetAsk / 3000);
  return Math.max(1, Math.min(5, score));
};

export const shapeBuyerPayload = (
  payload: InspectionReportPayload,
  visibleIssueNumbers: number[],
  visibility: BuyerVisibility,
): InspectionReportPayload => {
  const allowed = new Set(visibleIssueNumbers);
  const issueList = visibility.issuesList
    ? payload.issueList.filter((issue) => allowed.has(issue.issueNumber)).map((issue) => {
      const derived = issue.derived ? { ...issue.derived } : undefined;
      const shaped: InspectionIssue = { ...issue, derived };

      if (!visibility.costEstimates) {
        shaped.estimatedCost = undefined;
        if (shaped.derived) {
          shaped.derived.estimatedCostMin = undefined;
          shaped.derived.estimatedCostMax = undefined;
          shaped.derived.estimatedCostAvg = undefined;
          shaped.derived.contextualThresholdCost = undefined;
        }
      }

      if (!visibility.severity) {
        shaped.severity = undefined;
        if (shaped.derived) shaped.derived.normalizedSeverity = undefined;
      }

      if (!visibility.rationale) {
        shaped.source = undefined;
        shaped.fileRecco = undefined;
        shaped.aiRecco = undefined;
        shaped.potential_consequences = undefined;
        shaped.repair_approach = undefined;
        shaped.typicalBuyerAction = undefined;
        shaped.safety = undefined;
        shaped.typical_for_age = undefined;
        if (shaped.derived) {
          shaped.derived.safety = undefined;
          shaped.derived.typicalForAge = undefined;
          shaped.derived.categories = undefined;
          shaped.derived.tags = undefined;
        }
      }

      if (!visibility.photos) shaped.mediaRef = undefined;

      if (!visibility.specialists) {
        shaped.specialist_evaluation_needed = false;
        shaped.specialist_type = undefined;
        if (shaped.derived) shaped.derived.specialistNeeded = undefined;
      }

      return shaped;
    })
    : [];

  return {
    ...payload,
    issueList,
    summary: {
      ...payload.summary,
      totalIssues: issueList.length,
    },
    rollups: undefined,
  };
};
